import React, { useState } from 'react';
import { useTranslation } from '../libs/common/i18n';
import { getThemeColors } from '../libs/common/themeConfig';

/**
 * Get Twemoji CDN URL for an emoji
 * @param {string} emoji - Emoji character
 * @returns {string} Twemoji CDN URL
 */
export const getTwemojiUrl = (emoji) => {
  // Convert emoji to array of code points
  const codePoints = Array.from(emoji).map(char => char.codePointAt(0).toString(16));
  
  // Handle flag emojis (composed of two regional indicator symbols)
  if (codePoints.length === 2) {
    // Flag emoji: join code points with hyphen
    return `https://cdn.jsdelivr.net/npm/twemoji@latest/2/svg/${codePoints.join('-')}.svg`;
  }
  // Regular emoji: use first code point
  return `https://cdn.jsdelivr.net/npm/twemoji@latest/2/svg/${codePoints[0]}.svg`;
};

export const languages = [
  { code: 'en', name: 'English', emoji: '🇺🇸' },
  { code: 'zh', name: '中文', emoji: '🇨🇳' },
  { code: 'ja', name: '日本語', emoji: '🇯🇵' },
  { code: 'es', name: 'Español', emoji: '🇪🇸' },
  { code: 'pt', name: 'Português', emoji: '🇵🇹' },
  { code: 'ru', name: 'Русский', emoji: '🇷🇺' },
  { code: 'pl', name: 'Polski', emoji: '🇵🇱' },
  { code: 'uk', name: 'Українська', emoji: '🇺🇦' },
  { code: 'de', name: 'Deutsch', emoji: '🇩🇪' },
  { code: 'fr', name: 'Français', emoji: '🇫🇷' },
  { code: 'tr', name: 'Türkçe', emoji: '🇹🇷' },
  { code: 'sv', name: 'Svenska', emoji: '🇸🇪' },
  { code: 'nl', name: 'Nederlands', emoji: '🇳🇱' },
  { code: 'cs', name: 'Čeština', emoji: '🇨🇿' },
  { code: 'fi', name: 'Suomi', emoji: '🇫🇮' },
  { code: 'ro', name: 'Română', emoji: '🇷🇴' },
  { code: 'mn', name: 'Монгол', emoji: '🇲🇳' },
  { code: 'be', name: 'Беларуская', emoji: '🇧🇾' },
];

/**
 * Language selector dropdown
 */
export const LanguageSelector = () => {
  const { language, setLanguage } = useTranslation();
  const themeColors = getThemeColors();
  const [isOpen, setIsOpen] = useState(false);

  const displayLang = language;
  const currentLang = languages.find(l => l.code === displayLang) || languages[0];
  const currentLangName = currentLang.name;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 text-[0.8125rem] font-medium rounded transition-colors min-h-[2.5rem] min-w-[2.5rem] touch-manipulation flex items-center gap-2 hover:bg-[var(--hover-bg)]"
        style={{
          color: themeColors.text,
          backgroundColor: 'transparent',
          border: `1px solid ${themeColors.border}`,
          '--hover-bg': themeColors.hover,
        }}
        title={currentLangName}
      >
        <span className="hidden sm:inline">
          <img
            src={getTwemojiUrl(currentLang.emoji)}
            alt={currentLangName}
            style={{ width: '1rem', height: '1rem', display: 'inline-block', verticalAlign: 'middle' }}
            onError={(e) => {
              // Fallback to emoji if image fails to load
              e.target.style.display = 'none';
              e.target.parentElement.textContent = currentLang.emoji;
            }}
          />
        </span>
      </button>

      {isOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.5rem',
              minWidth: '10rem',
              maxWidth: '12rem',
              maxHeight: 'calc(100vh - 8rem)',
              overflowY: 'auto',
              backgroundColor: themeColors.surface,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '0.25rem',
              zIndex: 60,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full py-3 px-4 text-left transition-colors flex items-center gap-3 border-b min-h-[3rem] touch-manipulation ${
                  displayLang !== lang.code ? 'hover:bg-[var(--hover-bg)]' : ''
                }`}
                style={{
                  backgroundColor: displayLang === lang.code ? themeColors.accent + '20' : 'transparent',
                  borderBottomColor: themeColors.border,
                  '--hover-bg': themeColors.hover,
                }}
              >
                <img
                  src={getTwemojiUrl(lang.emoji)}
                  alt={lang.name}
                  style={{ width: '1.25rem', height: '1.25rem', display: 'inline-block', verticalAlign: 'middle' }}
                  onError={(e) => {
                    // Fallback to emoji if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentElement.textContent = lang.emoji;
                  }}
                />
                <span
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: displayLang === lang.code ? 600 : 400,
                    color: themeColors.text,
                  }}
                >
                  {lang.name}
                </span>
                {displayLang === lang.code && (
                  <svg
                    style={{ width: '1rem', height: '1rem', color: themeColors.accent, marginLeft: 'auto' }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
